import { HardDrive, CheckSquare, Plug } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { Connector, ConnectorStatus, ConnectorProvider } from '@/lib/api/types'

// --- Connector status ---

export const connectorStatusBadgeStyles: Record<ConnectorStatus, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  inactive: 'bg-gray-100 text-gray-600',
  error: 'bg-red-50 text-destructive',
}

export const connectorStatusDotStyles: Record<ConnectorStatus, string> = {
  active: 'bg-emerald-500',
  inactive: 'bg-gray-400',
  error: 'bg-destructive',
}

export const connectorStatusLabels: Record<ConnectorStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  error: 'Error',
}

// --- Provider config ---

export interface ProviderConfig {
  key: ConnectorProvider
  label: string
  description: string
  icon: LucideIcon
  iconBg: string
  iconColor: string
}

export const providers: ProviderConfig[] = [
  {
    key: 'google_drive',
    label: 'Google Drive',
    description: 'Sync files and folders',
    icon: HardDrive,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    key: 'clickup',
    label: 'ClickUp',
    description: 'Sync workspace docs',
    icon: CheckSquare,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
  },
]

export const fallbackProviderIcon = {
  icon: Plug,
  iconBg: 'bg-primary/10',
  iconColor: 'text-primary',
}

export function getProviderConfig(provider: ConnectorProvider): ProviderConfig {
  return providers.find((p) => p.key === provider) ?? {
    key: provider,
    label: provider,
    description: '',
    ...fallbackProviderIcon,
  }
}

// --- Row state (Phase 24) ---

/**
 * Card row state — wider than ConnectorStatus. Distinguishes "no connector
 * record" from "connector exists but OAuth not complete" via the authoritative
 * `hasCredentials` field on Connector (per API Phase 21).
 */
export type ConnectorRowState = 'unconnected' | 'auth-incomplete' | 'connected' | 'error'

export const rowStateBadgeStyles: Record<ConnectorRowState, string> = {
  unconnected: 'bg-gray-100 text-gray-600',
  'auth-incomplete': 'bg-amber-50 text-amber-700',
  connected: 'bg-emerald-50 text-emerald-700',
  error: 'bg-red-50 text-destructive',
}

export const rowStateDotStyles: Record<ConnectorRowState, string> = {
  unconnected: 'bg-gray-400',
  'auth-incomplete': 'bg-amber-500',
  connected: 'bg-emerald-500',
  error: 'bg-destructive',
}

export const rowStateLabels: Record<ConnectorRowState, string> = {
  unconnected: 'Not connected',
  'auth-incomplete': 'Auth incomplete',
  connected: 'Connected',
  error: 'Error',
}

/**
 * Derive the card row state from a connector record (or its absence).
 * Priority: error beats everything → no-credentials → connected.
 */
export function deriveRowState(connector: Connector | undefined): ConnectorRowState {
  if (!connector) return 'unconnected'
  if (connector.status === 'error') return 'error'
  if (!connector.hasCredentials) return 'auth-incomplete'
  return 'connected'
}
